import { ComboBox, Select, SelectItem, Tag } from '@carbon/react';
import { asProblem, ListTable, PageHeader, problemMessage, StatusTag, useFormat } from '@pos/admin-ui';
import { useTranslation } from '@pos/i18n';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ErrorNotice } from '../common/Notices';
import { brandsKey, listAllPages, scopeApis } from '../delivery/deliveryApi';
import {
  listMessages,
  MESSAGE_STATUSES,
  messagesKey,
  type CustomerMessage,
  type MessageChannel,
  type MessageEvent,
  type MessageStatus,
} from './messagingApi';
import './messaging.scss';

const STATUS_TAG = {
  pending: 'pending',
  sending: 'pending',
  sent: 'active',
  failed: 'failed',
  skipped: 'inactive',
  dead: 'failed',
} as const satisfies Record<MessageStatus, 'pending' | 'active' | 'failed' | 'inactive'>;

/**
 * What customers were told, and what wasn't sent and why (ADR 0072): a brand's messages, newest first, with the text or ZNS
 * template and values as sent. Messages taken by a test channel are marked "Test": nothing reached the customer. The phone and
 * the email address are masked by the server.
 */
export function CustomerMessagesPage() {
  const { t } = useTranslation();
  const { dateTime } = useFormat();
  const brands = useQuery({ queryKey: brandsKey, queryFn: () => listAllPages(scopeApis.brands) });
  const [brandId, setBrandId] = useState<string>();
  const brand = brandId ?? brands.data?.[0]?.id;
  const [status, setStatus] = useState('');
  const [paging, setPaging] = useState({ page: 1, pageSize: 25 });
  const messages = useQuery({
    queryKey: messagesKey(brand ?? '', status, paging.page, paging.pageSize),
    queryFn: () => listMessages(brand ?? '', status, paging.page, paging.pageSize),
    enabled: !!brand,
    refetchInterval: 10_000, // a test message shows its outcome within seconds
  });
  const values = (m: CustomerMessage) =>
    Object.entries(m.values)
      .map(([k, v]) => `${k}: ${v}`)
      .join(' · ');

  return (
    <section className="pos-admin-page">
      <PageHeader title={t('messaging.log.title')} description={t('messaging.log.intro')} />
      {brands.error && <ErrorNotice title={problemMessage(t, asProblem(brands.error))} onRetry={() => void brands.refetch()} />}
      <div className="pos-messages__filters">
        <ComboBox
          id="customer-messages-brand"
          titleText={t('messaging.templates.brand')}
          items={brands.data ?? []}
          itemToString={(b) => b?.name ?? ''}
          selectedItem={brands.data?.find((b) => b.id === brand) ?? null}
          onChange={({ selectedItem }) => {
            if (selectedItem) setBrandId(selectedItem.id);
            setPaging((p) => ({ ...p, page: 1 }));
          }}
        />
        <Select
          id="customer-messages-status"
          labelText={t('messaging.log.columns.status')}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPaging((p) => ({ ...p, page: 1 }));
          }}
        >
          <SelectItem value="" text={t('messaging.log.allStatuses')} />
          {MESSAGE_STATUSES.map((s) => (
            <SelectItem key={s} value={s} text={t(`messaging.status.${s}`)} />
          ))}
        </Select>
      </div>
      {brand && (
        <ListTable<CustomerMessage>
          ariaLabel={t('messaging.log.title')}
          loading={messages.isLoading}
          error={messages.error ? problemMessage(t, asProblem(messages.error)) : undefined}
          onRetry={() => void messages.refetch()}
          rows={messages.data?.items ?? []}
          empty={{ heading: t('messaging.log.empty') }}
          pagination={{ ...paging, total: Number(messages.data?.total ?? 0), onChange: setPaging }}
          columns={[
            { key: 'createdAt', header: t('messaging.log.columns.time'), render: (m) => dateTime(m.createdAt) },
            {
              key: 'eventCode',
              header: t('messaging.templates.columns.event'),
              render: (m) => (
                <>
                  {t(`messaging.events.${m.eventCode as MessageEvent}`)}
                  {m.sourceType === 'test' && <span className="pos-messages__secondary"> · {t('messaging.log.sampleMessage')}</span>}
                </>
              ),
            },
            {
              key: 'channel',
              header: t('messaging.templates.columns.channel'),
              render: (m) => (
                <>
                  {m.channel ? t(`messaging.channels.${m.channel as MessageChannel}`) : '—'}{' '}
                  {m.isTest && <Tag type="purple" size="sm">{t('messaging.log.test')}</Tag>}
                </>
              ),
            },
            { key: 'recipient', header: t('messaging.log.columns.recipient'), render: recipientOf },
            {
              key: 'content',
              header: t('messaging.templates.columns.content'),
              render: (m) => (
                <>
                  {m.content ?? '—'}
                  {Object.keys(m.values).length > 0 && <span className="pos-messages__secondary"> ({values(m)})</span>}
                </>
              ),
            },
            {
              key: 'status',
              header: t('messaging.log.columns.status'),
              render: (m) => (
                <>
                  <StatusTag status={STATUS_TAG[m.status as MessageStatus] ?? 'pending'}>{t(`messaging.status.${m.status as MessageStatus}`)}</StatusTag>
                  {m.lastError && <span className="pos-messages__secondary"> {t(`messaging.errors.${errorKey(m.lastError)}`)}</span>}
                </>
              ),
            },
          ]}
        />
      )}
    </section>
  );
}

/** The masked phone and, when the message has one, the masked email address (ADR 0125); the one its channel used first. */
function recipientOf(m: CustomerMessage): string {
  const phone = /\d/.test(m.recipient) ? m.recipient : null; // an email-only message has no phone ("***")
  const parts = m.channel === 'email' ? [m.recipientEmail, phone] : [phone, m.recipientEmail];
  return parts.filter(Boolean).join(' · ') || '—';
}

const ERRORS = ['no_messaging_account', 'no_template', 'recipient_unreachable', 'message_refused', 'account_rejected', 'unavailable'] as const;

/** The error's label key: the known codes (with or without `messaging.`), else "other". */
function errorKey(code: string): (typeof ERRORS)[number] | 'other' {
  const bare = code.replace(/^messaging\./, '');
  return ERRORS.find((e) => e === bare) ?? 'other';
}
